import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

export type TypedSupabaseClient = SupabaseClient<Database>;

let supabaseInstance: TypedSupabaseClient | null = null;

/**
 * Creates or returns a typed Supabase client singleton
 * @param supabaseUrl - Supabase project URL
 * @param supabaseAnonKey - Supabase anonymous key
 * @returns Typed Supabase client
 */
export function createTypedSupabaseClient(
  supabaseUrl: string,
  supabaseAnonKey: string
): TypedSupabaseClient {
  if (!supabaseInstance) {
    supabaseInstance = createClient<Database>(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
      // Add custom fetch to log 400 errors
      global: {
        fetch: async (url: RequestInfo | URL, options?: RequestInit) => {
          const response = await fetch(url, options);
          
          // Log 400 errors with full request details
          if (response.status >= 400) {
            console.error('🚨 Supabase Request Error:', {
              status: response.status,
              statusText: response.statusText,
              url: url.toString(),
              method: options?.method || 'GET',
              headers: options?.headers,
              body: options?.body,
            });
            
            // Clone response to read body without consuming original stream
            const clonedResponse = response.clone();
            try {
              const responseBody = await clonedResponse.text();
              console.error('🚨 Response Body:', responseBody);
            } catch (e) {
              console.error('Could not read response body:', e);
            }
          }
          
          return response;
        },
      },
    });
  }
  return supabaseInstance;
}

/**
 * Creates a new Supabase client instance (non-singleton)
 * Useful for server-side rendering or testing
 */
export function createSupabaseClient(
  supabaseUrl: string,
  supabaseAnonKey: string,
  options?: {
    accessToken?: string;
  }
): TypedSupabaseClient {
  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: typeof window !== 'undefined',
      detectSessionInUrl: typeof window !== 'undefined',
    },
    global: options?.accessToken
      ? {
          headers: {
            Authorization: `Bearer ${options.accessToken}`,
          },
        }
      : undefined,
  });
}

/**
 * Reset the singleton instance (useful for testing)
 */
export function resetSupabaseClient(): void {
  supabaseInstance = null;
}
